import { useId, useRef, useState } from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { api } from '../../services/api';

interface UploadResponse {
  success: true;
  data: { url: string };
}

interface ImageUploadFieldProps {
  value: string;
  onChange: (url: string) => void;
}

export function ImageUploadField({ value, onChange }: ImageUploadFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File): Promise<void> => {
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await api.post<UploadResponse>('/uploads/menu-item-image', formData);
      onChange(data.data.url);
    } catch (err) {
      // The axios interceptor already unwraps `{success:false,error}` bodies
      // into a friendly Error.message (e.g. "Unsupported image type ..."), so
      // surface that directly. Only fall back to the generic size hint for the
      // catch-all 500 (the most common cause is an image over the 10 MB limit).
      const message = err instanceof Error ? err.message : '';
      setError(
        message && message !== 'An unexpected error occurred'
          ? message
          : 'Upload failed — try a smaller image (max 10 MB).',
      );
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {value.length > 0 ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Menu item preview"
            className="h-32 w-32 rounded-xl object-cover ring-1 ring-wca-gray-light"
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -right-2 -top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-wca-black text-white shadow-md hover:bg-wca-red"
            aria-label="Remove image"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className={`flex h-32 w-32 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-wca-gray-mid/40 bg-wca-gray-light/40 text-wca-gray-mid transition-colors hover:border-wca-red hover:text-wca-red ${uploading ? 'pointer-events-none opacity-60' : ''}`}
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <>
              <ImagePlus className="h-6 w-6" />
              <span className="text-xs font-medium">Add photo</span>
            </>
          )}
        </label>
      )}

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="hidden"
        disabled={uploading}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      {value.length > 0 && !uploading && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs font-medium text-wca-red hover:text-wca-red-dark"
        >
          Replace photo
        </button>
      )}

      {error !== null && <p className="text-xs text-wca-red">{error}</p>}
      <p className="text-xs text-wca-gray-mid">
        JPEG, PNG, WEBP, or HEIC · max 10 MB · resized to 1200px.
      </p>
    </div>
  );
}
